import React, { useState, useMemo, useEffect } from 'react';
import { FormField } from '@/components/FormField';
import { Select } from '@/components/Select';
import { Card, SectionHeader } from '@/components/Card';
import { Info, MapPin, AlertTriangle, Target, FileText, ExternalLink, RefreshCw, Activity } from 'lucide-react';
import { BaseAdSetForm } from './BaseAdSetForm';
import { AttributionWindowSection, buildAttributionSpec } from './AttributionWindowSection';
import { ODAX_MATRIX, GOAL_LABEL, ConversionLocation } from './odaxMatrix';
import { facebookPageService } from '@/services/facebook-page.service';
import {
  FbPagePicker,
  FbPixelPicker,
  IgAccountPicker,
  WhatsAppNumberPicker,
  FbAppPicker,
  type PlacementValue,
} from '@/components/facebook/MetaResourcePickers';
import type { MetaApp } from '@/services/meta-resources.service';
import { appOsTargeting } from './appTargeting';

// Conversion events allowed for the ENGAGEMENT objective + WEBSITE location
// per Meta's ODAX matrix.
const ENG_WEBSITE_EVENTS = [
  'ADD_TO_WISHLIST',
  'CONTACT',
  'CUSTOMIZE_PRODUCT',
  'DONATE',
  'FIND_LOCATION',
  'SCHEDULE',
  'SEARCH',
  'START_TRIAL',
  'SUBMIT_APPLICATION',
  'SUBSCRIBE',
  'CONTENT_VIEW',
];

const ENGAGEMENT_LOCATIONS = ODAX_MATRIX.OUTCOME_ENGAGEMENT;

/**
 * Per-goal hints — surfaced inline below the goal dropdown so users know
 * what each goal *requires* before save. Most "Meta API rejected" errors
 * on engagement campaigns come from picking POST_ENGAGEMENT for a Page
 * with no posts, or THRUPLAY without a video creative.
 */
const GOAL_HINTS: Record<string, { tone: 'info' | 'warning'; body: string }> = {
  POST_ENGAGEMENT: {
    tone: 'warning',
    body: 'Requires the Page to have published posts already. If the Page is brand-new (or empty), Meta rejects this goal — pick the "On Page" location and Page Likes instead.',
  },
  PAGE_LIKES: {
    tone: 'info',
    body: 'Most permissive engagement goal. Works on any Page, regardless of post history. Recommended if you\'re unsure.',
  },
  THRUPLAY: {
    tone: 'warning',
    body: 'Requires the ad creative to be a video. Image / carousel ads will fail at activate time.',
  },
  TWO_SECOND_CONTINUOUS_VIDEO_VIEWS: {
    tone: 'warning',
    body: 'Requires a video creative. Optimises for 2-second-or-longer continuous views.',
  },
  EVENT_RESPONSES: {
    tone: 'warning',
    body: 'Requires the Page to host a published Facebook Event. The "On Event" location is for promoting that event.',
  },
  CONVERSATIONS: {
    tone: 'info',
    body: 'Optimises for chats started from the ad. Pick the destination + linked Page below.',
  },
  LINK_CLICKS: {
    tone: 'info',
    body: 'Optimises for clicks on the ad\'s CTA link. Cheaper than Conversations but less qualified.',
  },
  IMPRESSIONS: {
    tone: 'info',
    body: 'Optimises for total impressions. Cheap reach, no engagement signal.',
  },
  REACH: {
    tone: 'info',
    body: 'Optimises for unique people reached, not impressions. Use when frequency matters.',
  },
};

// Legacy WhatsApp Click-to-Convo app id. Newer Meta accounts may reject
// this — we keep sending it for backward compat but the safer fix is for

export const EngagementAdSetForm: React.FC = () => {
  const [location, setLocation] = useState<ConversionLocation>('ON_POST');
  const [optGoal, setOptGoal] = useState<string>('POST_ENGAGEMENT');
  const [pageId, setPageId] = useState('');
  const [igActorId, setIgActorId] = useState<string | null>(null);
  const [waPhoneId, setWaPhoneId] = useState('');
  const [waPhoneDigits, setWaPhoneDigits] = useState('');
  const [fbAppId, setFbAppId] = useState('');
  const [pickedApp, setPickedApp] = useState<MetaApp | null>(null);
  const objectStoreUrl =
    pickedApp?.objectStoreUrls?.google_play ??
    pickedApp?.objectStoreUrls?.iphone_app_store ??
    pickedApp?.objectStoreUrls?.amazon_app_store ??
    '';
  const [placement, setPlacement] = useState<PlacementValue>({ mode: 'automatic' });
  // Meta rejected 7d click for OUTCOME_ENGAGEMENT live (2026-08-28, subcode
  // 1885423 — "supported values is/are attribution window of 1 day(s)").
  const [clickWin, setClickWin] = useState('1');
  const [viewWin, setViewWin] = useState('0');
  const [pixelId, setPixelId] = useState('');
  const [eventType, setEventType] = useState('CONTENT_VIEW');

  const locationEntry = useMemo(
    () => ENGAGEMENT_LOCATIONS.find((l) => l.value === location)!,
    [location],
  );

  // ── Empty-page detection ────────────────────────────────────────────────
  // POST_ENGAGEMENT requires the Page to have published posts. Most users
  // don't realise this until Meta rejects the save. We pre-check by listing
  // the Page's posts as soon as a Page is selected for an ON_POST adset.
  const [postCount, setPostCount] = useState<number | null>(null);
  const [pageHasPostsCheckDone, setPageHasPostsCheckDone] = useState(false);
  const [pageNameForBanner, setPageNameForBanner] = useState<string | null>(null);

  const isOnPostLocation = location === 'ON_POST';
  const needsPageWithPosts = isOnPostLocation && optGoal === 'POST_ENGAGEMENT';

  useEffect(() => {
    if (!needsPageWithPosts || !pageId) {
      setPostCount(null);
      setPageHasPostsCheckDone(false);
      return;
    }
    let cancelled = false;
    setPageHasPostsCheckDone(false);
    facebookPageService.posts(false, 5, pageId)
      .then((r) => {
        if (cancelled) return;
        setPostCount(r.data?.length ?? 0);
        setPageNameForBanner(r.pageName ?? null);
        setPageHasPostsCheckDone(true);
      })
      .catch(() => {
        // Non-blocking — if we can't check, fall through and let Meta validate
        // at save time. The save-failure rewriter still kicks in.
        if (!cancelled) setPageHasPostsCheckDone(true);
      });
    return () => { cancelled = true; };
  }, [pageId, needsPageWithPosts]);

  const showEmptyPageBanner = needsPageWithPosts
    && pageHasPostsCheckDone
    && postCount === 0;

  // Auto-snap goal to the first valid one when location changes.
  useEffect(() => {
    if (!locationEntry.goals.includes(optGoal)) {
      setOptGoal(locationEntry.goals[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const goalOptions = locationEntry.goals.map((g) => ({
    code: g,
    label: GOAL_LABEL[g] ?? g,
  }));

  const isMessenger = location === 'MESSENGER';
  const isWhatsApp = location === 'WHATSAPP';
  const isInstagram = location === 'INSTAGRAM';
  const isWebsiteOrApp = location === 'WEBSITE' || location === 'APP';
  const needsPagePicker = !isWebsiteOrApp;
  const needsPixel = location === 'WEBSITE'
    && (optGoal === 'OFFSITE_CONVERSIONS' || optGoal === 'ONSITE_CONVERSIONS');

  // ON_VIDEO / video objectives only allow 1d click. Auto-clamp.
  useEffect(() => {
    if (location === 'ON_VIDEO') {
      if (clickWin !== '1') setClickWin('1');
      if (viewWin !== '0') setViewWin('0');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  const igAllowed =
    placement.mode === 'automatic' ||
    !placement.publisher_platforms ||
    placement.publisher_platforms.includes('instagram');

  const extraPayload = () => {
    const out: Record<string, unknown> = {};
    const promoted: Record<string, string> = {};

    // On-post / page / video goals need the Page in promoted_object — a ThruPlay ad without it fails at ad create
    // with "Your campaign must include an ad set with a selected object to promote" (live, 2026-09-29).
    const needsPagePromoted: ConversionLocation[] = ['ON_POST', 'ON_PAGE', 'ON_VIDEO', 'ON_EVENT'];
    if (pageId && needsPagePromoted.includes(location)) {
      promoted.page_id = pageId;
    }

    if (locationEntry.destinationType) {
      out.destination_type = locationEntry.destinationType;
    }

    // Click-to-WhatsApp takes the Page plus the number's digits; the old hard-coded WhatsApp application_id was
    // refused with "(#900) invalid app id" (live, 2026-09-29).
    if (isWhatsApp) {
      if (pageId) promoted.page_id = pageId;
      if (waPhoneDigits) promoted.whatsapp_phone_number = waPhoneDigits;
    }
    // Messenger / Instagram-DM still need page_id even though they're
    // explicit-destination locations.
    if ((isMessenger || isInstagram) && pageId) {
      promoted.page_id = pageId;
    }

    if (needsPixel && pixelId) {
      promoted.pixel_id = pixelId;
      promoted.custom_event_type = eventType;
    }

    // App destination: Meta refuses the ad set without the app ("Application is required in Promoted Object").
    if (location === 'APP' && fbAppId) {
      promoted.application_id = fbAppId;
      if (objectStoreUrl) promoted.object_store_url = objectStoreUrl;
    }

    if (Object.keys(promoted).length > 0) out.promoted_object = promoted;
    // instagram_actor_id is a creative-level field — Meta + our DTO reject it
    // on the ad-set payload. The IG account flows through the creative.
    out.attribution_spec = buildAttributionSpec(clickWin, viewWin);
    return out;
  };

  const validateExtra = () => {
    if (needsPagePicker && !pageId) return 'Pick a Facebook Page first';
    if (isWhatsApp && !waPhoneId) return 'Pick a WhatsApp number for the chat destination';
    if (needsPixel && !pixelId) return 'Pick a Meta Pixel — required for Conversions optimisation';
    if (location === 'APP' && !fbAppId) return 'Pick a Facebook App for the App destination';
    return null;
  };

  const extraTargeting = () => (location === 'APP' ? appOsTargeting(objectStoreUrl) : undefined);

  return (
    <BaseAdSetForm
      optimizationGoals={goalOptions}
      extraPayload={extraPayload}
      extraTargeting={extraTargeting}
      validateExtra={validateExtra}
      onPlacementChange={setPlacement}
      onOptimizationGoalChange={setOptGoal}
      expectedObjective="OUTCOME_ENGAGEMENT"
    >
      {/* Conversion-location picker drives which optimisation goals are
          valid for this objective per Meta's ODAX matrix. */}
      <Card padded className="!mt-5">
        <SectionHeader
          title="Where the engagement happens"
          icon={<Target size={16} />}
          subtitle="Different locations unlock different optimisation goals. Pick the closest match."
        />
        <FormField label="Conversion location" required>
          <Select
            value={location}
            onChange={(e) => setLocation(e.target.value as ConversionLocation)}
            className="h-10 w-full rounded-lg border border-border bg-surface px-3 text-sm focus:border-primary focus:outline-none focus:ring-4 focus:ring-primary-soft"
          >
            {ENGAGEMENT_LOCATIONS.map((l) => (
              <option key={l.value} value={l.value}>{l.label}</option>
            ))}
          </Select>
        </FormField>
      </Card>

      {/* Empty-page banner — fires when the Page picked has no posts and the
          user is trying to use POST_ENGAGEMENT (which requires posts). Shows
          BEFORE save so the user doesn't waste time and hit Meta's
          confusing rejection. */}
      {showEmptyPageBanner && (
        <div className="flex items-start gap-3 rounded-2xl border border-warning/40 bg-warning-soft px-4 py-3.5">
          <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-warning text-white">
            <FileText size={18} />
          </div>
          <div className="min-w-0 flex-1 space-y-2">
            <div>
              <div className="font-semibold text-text-primary">
                {pageNameForBanner ? `"${pageNameForBanner}"` : 'This Page'} has no published posts yet
              </div>
              <p className="mt-0.5 text-xs text-text-secondary leading-relaxed">
                Post Engagement ads optimise for likes, comments, shares, and saves on existing posts.
                Without at least one post on the Page, Meta has nothing to engage with — and will reject this ad set at save time.
              </p>
            </div>

            <div className="rounded-lg border border-warning/30 bg-surface px-3 py-2.5">
              <div className="text-xs font-semibold text-text-primary mb-1.5">Two ways forward</div>
              <ol className="list-decimal pl-4 space-y-1 text-xs text-text-secondary leading-relaxed">
                <li>
                  <strong className="text-text-primary">Recommended — publish a post first:</strong>{' '}
                  Go to your Page on Facebook, share something engaging (a photo, an offer, a story about your business),
                  wait ~5 minutes for Meta to index it, then come back here and click Refresh below.
                </li>
                <li>
                  <strong className="text-text-primary">Skip posts entirely:</strong>{' '}
                  Switch <strong>Conversion location</strong> to <strong>"On Page — invite Page likes"</strong>.
                  That uses Page Likes optimisation which works on any Page, even brand-new empty ones.
                </li>
              </ol>
            </div>

            <div className="flex flex-wrap items-center gap-2 pt-1">
              {pageId && (
                <a
                  href={`https://facebook.com/${pageId}`}
                  target="_blank"
                  rel="noreferrer"
                  className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-surface-alt"
                >
                  Open Page on Facebook <ExternalLink size={11} />
                </a>
              )}
              <button
                type="button"
                onClick={() => {
                  if (!pageId) return;
                  setPageHasPostsCheckDone(false);
                  facebookPageService.posts(true, 5, pageId)
                    .then((r) => {
                      setPostCount(r.data?.length ?? 0);
                      setPageHasPostsCheckDone(true);
                    })
                    .catch(() => setPageHasPostsCheckDone(true));
                }}
                className="inline-flex items-center gap-1 rounded-lg border border-border bg-surface px-2.5 py-1 text-xs font-medium text-text-secondary hover:bg-surface-alt"
              >
                <RefreshCw size={11} /> I posted — recheck
              </button>
              <button
                type="button"
                onClick={() => setLocation('ON_PAGE')}
                className="inline-flex items-center gap-1 rounded-lg border border-primary bg-primary text-on-primary px-2.5 py-1 text-xs font-semibold hover:bg-primary-hover"
              >
                Switch to Page Likes
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Goal hint — hide when the empty-page banner is up to avoid duplicate
          warnings about the same root cause. */}
      {GOAL_HINTS[optGoal] && !showEmptyPageBanner && (
        <div
          className={
            'flex items-start gap-2 rounded-lg border px-3 py-2.5 text-xs ' +
            (GOAL_HINTS[optGoal].tone === 'warning'
              ? 'border-warning/40 bg-warning-soft'
              : 'border-info/40 bg-info-soft')
          }
        >
          {GOAL_HINTS[optGoal].tone === 'warning'
            ? <AlertTriangle size={14} className="mt-0.5 shrink-0 text-warning" />
            : <Info size={14} className="mt-0.5 shrink-0 text-info" />}
          <p className="text-text-secondary leading-relaxed">{GOAL_HINTS[optGoal].body}</p>
        </div>
      )}

      {isWhatsApp && (
        <FormField label="WhatsApp number" required hintBelow hint="One of your saved WhatsApp business numbers">
          <WhatsAppNumberPicker value={waPhoneId} onChange={(id, num) => { setWaPhoneId(id); setWaPhoneDigits((num?.displayPhoneNumber ?? '').replace(/\D/g, '')); }} />
        </FormField>
      )}

      {location === 'APP' && (
        <FormField label="Facebook App" required hintBelow hint="Pick from apps you've registered at developers.facebook.com.">
          <FbAppPicker value={fbAppId} mobileOnly onChange={(id, app) => { setFbAppId(id); setPickedApp(app); }} />
        </FormField>
      )}

      {isMessenger && (
        <div className="flex items-start gap-2 rounded-lg border border-info/40 bg-info-soft px-3 py-2.5 text-xs">
          <Info size={14} className="mt-0.5 shrink-0 text-info" />
          <p className="text-text-secondary">
            Click-to-Messenger uses your Page's Messenger thread. On the next step,
            set the ad creative's CTA to <strong>Message Page</strong>. Replies arrive in your Page inbox in Meta Business Suite — SlightorCRM has no Messenger or Instagram DM inbox yet.
          </p>
        </div>
      )}

      {/* Pixel + event picker — only when WEBSITE location with a
          conversion goal. Without these Meta has no way to know what
          counts as engagement on the site. */}
      {needsPixel && (
        <Card padded className="!mt-5">
          <SectionHeader
            title="Pixel & conversion event"
            icon={<Activity size={16} />}
            subtitle="Tracks when someone engages on your website. Without a Pixel + an event firing, Conversions optimisation can't run."
          />
          <FormField label="Meta Pixel" required hintBelow hint="Pick a pixel installed on your destination site.">
            <FbPixelPicker value={pixelId} onChange={(id) => setPixelId(id)} />
          </FormField>
          <FormField label="Conversion event" required hintBelow hint="Which event on your site counts as engagement.">
            <Select value={eventType} onChange={(e) => setEventType(e.target.value)}>
              {ENG_WEBSITE_EVENTS.map((ev) => (
                <option key={ev} value={ev}>{ev.replace(/_/g, ' ')}</option>
              ))}
            </Select>
          </FormField>
          <p className="mt-1.5 text-2xs text-text-muted leading-relaxed">
            <Info size={11} className="inline mr-0.5" />
            The selected event must already be set up on your Pixel and have fired at least once.
            If you haven\'t set this up, switch the conversion location to{' '}
            <strong className="text-text-primary">"On Post"</strong> or{' '}
            <strong className="text-text-primary">"On Page"</strong> — those don\'t need a Pixel.
          </p>
        </Card>
      )}

      {/* Page picker — needed for everything except WEBSITE / APP destinations
          (those use a Pixel or app_id and don't need promoted_object.page_id). */}
      {needsPagePicker && (
        <Card padded className="!mt-5">
          <SectionHeader
            title="Where ads will run"
            icon={<MapPin size={16} />}
            subtitle={
              location === 'ON_PAGE'    ? 'The Page that recipients will be invited to like.'
              : location === 'ON_POST'  ? 'The Page whose posts will be promoted.'
              : location === 'ON_VIDEO' ? 'The Page whose video creative will run.'
              : location === 'ON_EVENT' ? 'The Page that hosts the event.'
              : 'The Page the conversation will run on.'
            }
          />
          <FormField label="Facebook Page" required>
            <FbPagePicker value={pageId} restrictToAuthorized onChange={(id) => setPageId(id)} />
          </FormField>
          <FormField label="Instagram (optional)">
            <IgAccountPicker
              value={igActorId}
              onChange={(id) => setIgActorId(id)}
              disabled={!igAllowed || isInstagram}
              disabledReason={
                isInstagram
                  ? 'For Instagram-DM destination, the IG account is set on the Page in Meta Business Suite.'
                  : 'Instagram is excluded from your Placements selection. Add Instagram under Manual placements to enable this.'
              }
            />
          </FormField>
        </Card>
      )}

      <AttributionWindowSection
        clickWin={clickWin}
        setClickWin={setClickWin}
        viewWin={viewWin}
        setViewWin={setViewWin}
        clickOptions={['1']}
        hideViewThrough
        hint="Engagement campaigns only accept a 1-day click window — Meta rejects everything else."
      />
    </BaseAdSetForm>
  );
};
